import { ConsumesOptions } from './types';
import { attachShareScopeMap } from './attachShareScopeMap';
import { updateConsumeOptions } from './updateOptions';
import { getUsedExports } from './getUsedExports';
import type { Shared } from '@module-federation/runtime/types';
import { getShortErrorMsg, RUNTIME_012 } from '@module-federation/error-codes';

export function consumes(options: ConsumesOptions) {
  updateConsumeOptions(options);
  const {
    chunkId,
    promises,
    installedModules,
    webpackRequire,
    chunkMapping,
    moduleToHandlerMapping,
  } = options;

  attachShareScopeMap(webpackRequire);
  if (webpackRequire.o(chunkMapping, chunkId)) {
    chunkMapping[chunkId].forEach((id) => {
      if (webpackRequire.o(installedModules, id)) {
        return promises.push(installedModules[id] as Promise<any>);
      }
      const onFactory = (factory: () => any) => {
        installedModules[id] = 0;
        webpackRequire.m[id] = (module) => {
          delete webpackRequire.c[id];
          const result = factory();
          // Add layer property from shareConfig if available
          const { shareInfo } = moduleToHandlerMapping[id];
          if (
            shareInfo?.shareConfig?.layer &&
            result &&
            typeof result === 'object'
          ) {
            try {
              // Only set layer if it's not already defined or if it's undefined
              if (
                !result.hasOwnProperty('layer') ||
                (result as any).layer === undefined
              ) {
                (result as any).layer = shareInfo.shareConfig.layer;
              }
            } catch (e) {
              // Ignore if layer property is read-only
            }
          }
          module.exports = result;
        };
      };
      const onError = (error: unknown) => {
        delete installedModules[id];
        webpackRequire.m[id] = (module) => {
          delete webpackRequire.c[id];
          throw error;
        };
      };
      try {
        const federationInstance = webpackRequire.federation.instance;
        if (!federationInstance) {
          throw new Error('Federation instance not found!');
        }
        const { shareKey, getter, shareInfo, treeShakingGetter } =
          moduleToHandlerMapping[id];
        const usedExports = getUsedExports(webpackRequire, shareKey);
        const customShareInfo: Partial<Shared> = { ...shareInfo };
        // compatibility for rspack, which will wrap scope with array, but webpack will not
        // https://github.com/web-infra-dev/rspack/blob/main/packages/rspack/src/runtime/moduleFederationDefaultRuntime.js#L95
        if (
          Array.isArray(customShareInfo.scope) &&
          Array.isArray(customShareInfo.scope[0])
        ) {
          customShareInfo.scope = customShareInfo.scope[0];
        }
        if (usedExports) {
          customShareInfo.treeShaking = {
            usedExports,
            useIn: [federationInstance.options.name],
          };
        }
        const promise = federationInstance
          .loadShare(shareKey, {
            customShareInfo,
            context: {
              trigger: 'build',
              moduleId: id,
              chunkId,
            },
          })
          .then((factory: any) => {
            if (factory === false) {
              if (typeof getter !== 'function') {
                throw new Error(
                  getShortErrorMsg(
                    RUNTIME_012,
                    {
                      [RUNTIME_012]:
                        'The getter for the shared module is not a function. This may be caused by setting "shared.import: false" without the host providing the corresponding lib.',
                    },
                    { shareKey },
                  ),
                );
              }
              return treeShakingGetter?.() || getter();
            }
            return factory;
          });

        if (promise.then) {
          promises.push(
            (installedModules[id] = promise.then(onFactory).catch(onError)),
          );
        } else {
          // @ts-ignore maintain previous logic
          onFactory(promise);
        }
      } catch (e) {
        onError(e);
      }
    });
  }
}
